import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { CardContent } from '../ui/card';
import { Image, Upload, Plus, File, Video, X, Check } from 'lucide-react';
import { useState } from 'react';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../../hooks/use-toast';
import * as z from 'zod';

export const mediaFormSchema = z.object({
  thumbnail: z.any().refine(val => !!val, {
    message: "Thumbnail is required.",
  }),
  introVideo: z.any().optional(),
  syllabusMaterial: z.any().refine(val => !!val, {
    message: "Syllabus Material is required.",
  }),
});

export type MediaFormValues = z.infer<typeof mediaFormSchema>;

interface MediaTabProps {
  formData: {
    thumbnail: any;
    thumbnailPreview: string | null;
    introVideo?: File | null;
    syllabusMaterial?: File | any;
  };
  handleSyllabusMaterialChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleThumbnailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearThumbnail: () => void;
  clearSyllabusMaterial: () => void;
  form: any;
}

const MediaTab = ({ formData, handleThumbnailChange, handleSyllabusMaterialChange, clearThumbnail, clearSyllabusMaterial, form }: MediaTabProps) => {
  const [uploadedIntroVideo, setUploadedIntroVideo] = useState<File | null>(null);
  const [uploadedMaterials, setUploadedMaterials] = useState<File[]>([]);

  const handleIntroVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedIntroVideo(file);
      form.setValue('introVideo', file);
      toast({
        description: `Video "${file.name}" uploaded successfully`,
        variant: "default",
      });
    }
  };

  const handleMaterialsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setUploadedMaterials(prev => [...prev, ...newFiles]);
      form.setValue('syllabusMaterial', [...uploadedMaterials, ...newFiles]);
      toast({
        description: `${newFiles.length} file(s) uploaded successfully`,
        variant: "default",
      });
    }
  };

  const removeIntroVideo = () => {
    setUploadedIntroVideo(null);
    form.setValue('introVideo', null);
  };

  const removeMaterial = (index: number) => {
    const updatedMaterials = uploadedMaterials.filter((_, i) => i !== index);
    setUploadedMaterials(updatedMaterials);
    form.setValue('syllabusMaterial', updatedMaterials);
  };

  return (
    <CardContent className="space-y-6 pt-2">
      <Form {...form}>
        <form className="space-y-6">
          <FormField
            control={form.control}
            name="thumbnail"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Course Thumbnail</FormLabel>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-1/3">
                    <div className="aspect-video bg-gray-100 rounded-md overflow-hidden flex items-center justify-center">
                      {formData.thumbnailPreview ? (
                        <div className="relative w-full h-full">
                          <img
                            src={formData.thumbnailPreview}
                            alt="Course thumbnail"
                            className="w-full h-full object-cover"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full bg-black/40 text-white hover:bg-black/60"
                            onClick={() => {
                              clearThumbnail();
                              form.setValue('thumbnail', null);
                            }}
                            type="button"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center p-4">
                          <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No thumbnail</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <div>
                      <input
                        id="thumbnail"
                        name="thumbnail"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          handleThumbnailChange(e);
                          field.onChange(e.target.files?.[0] || null);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('thumbnail')?.click()}
                        className="mb-2"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Thumbnail
                      </Button>
                      <FormDescription className="text-xs text-gray-500">
                        Recommended size: 1280x720 pixels (16:9 aspect ratio)
                      </FormDescription>
                    </div>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="introVideo"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Introduction Video(Not Available Currenlty)</FormLabel>
                <div className="border-2 border-dashed border-gray-200 rounded-md p-6">
                  {uploadedIntroVideo ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded"
                    >
                      <div className="flex items-center gap-3">
                        <Video className="h-6 w-6 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium">{uploadedIntroVideo.name}</p>
                          <p className="text-xs text-gray-500">
                            {(uploadedIntroVideo.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeIntroVideo}
                        type="button"
                      >
                        <X size={16} />
                      </Button>
                    </motion.div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <Video className="h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Drag and drop a video file here, or click to browse</p>
                      <input
                        id="video"
                        name="video"
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={handleIntroVideoChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('video')?.click()}
                        className="mt-4"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Video
                      </Button>
                    </div>
                  )}
                </div>
                <FormDescription className="text-xs text-gray-500 mt-1">
                  Optional: Add an introduction video to your course
                </FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="syllabusMaterial"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Additional Material</FormLabel>
                <div className="border-2 border-dashed border-gray-200 rounded-md p-6">
                  {formData?.syllabusMaterial?.url !== null ? (
                    <div className="space-y-2">
                      <motion.div
                        key={0} // Since it's a single file, we can use a fixed key.  
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0 * 0.05 }}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded"
                      >
                        <div className="flex items-center gap-3">
                          <File className="h-6 w-6 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{formData?.syllabusMaterial?.fileName}</p>
                            <p className="text-xs text-gray-500">
                              {(formData?.syllabusMaterial?.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            clearSyllabusMaterial(); // Function to clear the selected material  
                            form.setValue('syllabusMaterial', null); // Reset the form value  
                          }}
                          type="button"
                        >
                          <X size={16} />
                        </Button>
                      </motion.div>
                      <div className="flex justify-center mt-4">
                        <input
                          id="material"
                          name="material"
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleSyllabusMaterialChange(e); // Upload the file  
                              field.onChange(e.target.files[0]); // Update the form  
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('material')?.click()}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Replace File
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <File className="h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Upload a PDF worksheet or supplementary material</p>
                      <input
                        id="material"
                        name="material"
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleSyllabusMaterialChange(e);
                            field.onChange(e.target.files[0]); // Set the form value  
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('material')?.click()}
                        className="mt-4"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Upload File
                      </Button>
                    </div>
                  )}
                </div>
                <FormDescription className="text-xs text-gray-500 mt-1">
                  Optional: Add a PDF, worksheet, or other supplementary material.
                </FormDescription>
              </FormItem>
            )}
          />
        </form>
      </Form>
    </CardContent>
  );
};

export default MediaTab;